/**
 * Debug Logger Utility
 * 
 * A centralized utility for consistent debug logging with level control.
 * This allows us to easily control which types of logs are displayed.
 */

// Only enable debug in development mode
export const DEBUG_ENABLED = process.env.NODE_ENV === 'development';

// Log levels for different types of events
export type LogLevel = 'action' | 'render' | 'state' | 'network' | 'important' | 'progress' | 'search' | 'button';

// Configure which types of logs to show - disable verbose logs by default
const ENABLED_LOG_TYPES: Record<LogLevel, boolean> = {
  action: true,     // User interactions (keep enabled)
  render: false,    // Component rendering (very noisy, disabled)
  state: false,     // State updates (very noisy, disabled by default)
  network: true,    // API calls (important to track)
  important: true,  // Always show these logs
  progress: false,  // Progress updates (very noisy, disabled by default)
  search: false,    // Search-related logs (disabled by default, enable for debugging search)
  button: true,     // Button-related logs (enable for debugging buttons)
};

// Limit debug to specific movies to reduce console spam
const DEBUG_MOVIE_TITLES = ['Inception', 'The Matrix'];

/**
 * Helper for consistent debug movie identification
 * Only enables logs for specific important movies to reduce console spam
 */
export const isDebugMovie = (movie: any): boolean => {
  if (!movie || !DEBUG_ENABLED) return false;
  
  // If it's an explicitly tracked movie title
  if (movie.title && DEBUG_MOVIE_TITLES.includes(movie.title)) {
    return true;
  }
  
  return false;
};

/**
 * Debug log function with level control
 */
export const debugLog = (
  level: LogLevel,
  message: string,
  ...data: any[]
) => {
  if (DEBUG_ENABLED && ENABLED_LOG_TYPES[level]) {
    const timestamp = new Date().toISOString().split('T')[1].substring(0, 8);
    const prefix = level === 'important' ? '🔍' : (level === 'search' ? '🔎' : '');
    console.log(`[${timestamp}][${level.toUpperCase()}] ${prefix}${message}`, ...data);
  }
};

/**
 * Special log function for progress updates
 * Only logs when crossing specific thresholds to reduce noise
 */
export const logProgress = (
  progressValue: number, 
  movieTitle: string, 
  actionId: string
): void => {
  // Define threshold points for logging (to reduce noise)
  const thresholds = [100, 75, 50, 25, 10, 0];
  
  // Find the nearest threshold
  const nearestThreshold = thresholds.find(t => progressValue >= t && progressValue < t + 5);
  
  // Only log when crossing a threshold or at exactly 0
  if (nearestThreshold !== undefined || progressValue === 0) {
    debugLog('progress', `Progress [${progressValue.toFixed(0)}%] for "${movieTitle}" (${actionId})`);
  }
};

/**
 * Special log function for important interactions that should always be logged
 * in development environment
 */
export const logAction = (message: string, ...args: any[]): void => {
  // Only log in development mode
  if (DEBUG_ENABLED) {
    // Check if any args are debug movies
    const shouldLog = args.some(arg => 
      typeof arg === 'object' && 
      arg?.title && 
      isDebugMovie(arg)
    );
    
    if (shouldLog) {
      const timestamp = new Date().toISOString().split('T')[1].substring(0, 8);
      console.log(`[${timestamp}] ⚡ ${message}`, ...args);
    }
  }
};

/**
 * Log network operations (API calls, DB operations)
 */
export const logNetwork = (message: string, ...data: any[]) => {
  debugLog('network', message, ...data);
};

/**
 * Log render information (used sparingly to avoid noise)
 */
export const logRender = (message: string, ...data: any[]) => {
  debugLog('render', message, ...data);
};

/**
 * Log state updates
 */
export const logState = (message: string, ...data: any[]) => {
  debugLog('state', message, ...data);
};

/**
 * Log search-related operations with special formatting
 */
export const logSearch = (message: string, ...data: any[]) => {
  debugLog('search', message, ...data);
};

/**
 * Log button state changes
 */
export const logButtonState = (
  context: string,
  intent: string,
  state: Record<string, any>
): void => {
  if (DEBUG_ENABLED && ENABLED_LOG_TYPES['button']) {
    const timestamp = new Date().toISOString().split('T')[1].substring(0, 8);
    console.log(`[${timestamp}][BUTTON] 🔘 ${context}/${intent}:`, state);
  }
};

/**
 * Enable a specific log level temporarily
 */
export const enableLogLevel = (level: LogLevel, enable: boolean = true) => {
  if (DEBUG_ENABLED) {
    ENABLED_LOG_TYPES[level] = enable;
    console.log(`[DEBUG] ${level} logs ${enable ? 'enabled' : 'disabled'}`);
  }
};

/**
 * Helper to only log meaningful search queries
 * Avoids logging empty or too-short queries
 */
export const logSearchIfMeaningful = (query: string, message: string, ...data: any[]) => {
  if (DEBUG_ENABLED && ENABLED_LOG_TYPES['search'] && query && query.length >= 2) {
    const timestamp = new Date().toISOString().split('T')[1].substring(0, 8);
    console.log(`[${timestamp}][SEARCH] 🔎 ${message}`, ...data);
  }
};
